import { useQuery } from "@apollo/client";
import { Icon, SearchIcon, AddIcon } from "@chakra-ui/icons";
import {
  SimpleGrid,
  Flex,
  InputGroup,
  InputLeftAddon,
  Input,
  Popover,
  PopoverTrigger,
  Button,
  Portal,
  PopoverContent,
  PopoverArrow,
  PopoverHeader,
  PopoverCloseButton,
  PopoverBody,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  ButtonGroup,
  IconButton,
  Stack,
  Text,
  Box,
  Table,
  Th,
  Thead,
  Tr,
  Td,
  Tbody,
  Center,
  Spinner,
  useMediaQuery,
} from "@chakra-ui/react";
import { debounce, get } from "lodash";
import React, { useCallback, useState } from "react";
import { Helmet } from "react-helmet";
import { FaSync } from "react-icons/fa";
import { MdFilterList } from "react-icons/md";
import { GET_EXTERNAL_COMMODITIES_PUBLIC } from "../../pages/public/queries";
import Pagination from "../common/Pagination";

type PublicCommoditiesTableProps = {
  isPublic?: boolean;
  similar_categories?: string[];
  showFilters?: boolean;
};

const Clone = (props: PublicCommoditiesTableProps) => {
  const {
    isPublic = true,
    similar_categories = [],
    showFilters = true,
  } = props;

  const [isLargerThan900] = useMediaQuery("(min-width: 900px)");
  const [search, setSearch] = useState("");
  const [refetchLoading, setRefetchLoading] = useState(false);

  const [page, setPage] = useState(1);

  const [range, setRange] = useState<{
    min: number | any;
    max?: number | any;
  }>({
    min: 1,
  });

  const { data, loading, refetch } = useQuery(GET_EXTERNAL_COMMODITIES_PUBLIC, {
    variables: {
      args: {
        ...range,
        query: search,
        pagination: {
          page,
          size: 20,
        },
      },
    },
    fetchPolicy: "network-only",
  });

  const debounceFn = useCallback(
    debounce((val) => {
      setSearch(val);
      setPage(1);
    }, 500),
    []
  );

  const debounceRange = useCallback(
    debounce((val) => {
      setRange(val);
      setPage(1);
    }, 500),
    []
  );

  const onSearch = (val: any) => {
    debounceFn(val);
  };

  const onChangeRange = (val: any) => {
    debounceRange(val);
  };

  const refetchCommodities = () => {
    setRefetchLoading(true);
    refetch()
      .then(() => setRefetchLoading(false))
      .finally(() => setRefetchLoading(false));
  };

  const commodities = get(data, "publicExternalCommodities.items", []);
  const meta = get(data, "publicExternalCommodities.meta", []);

  return (
    <>
      <Helmet>
        <title>Freeshu | Dashboard</title>
      </Helmet>

      {showFilters && (
        <Box p="5">
          <SimpleGrid columns={[1, 2]}>
            <Pagination
              meta={meta}
              page={page}
              setPage={setPage}
              isDisabled={loading}
            />

            <Flex alignItems="center" justifyContent="space-between">
              <InputGroup mx="2">
                <InputLeftAddon
                  rounded="md"
                  children={<Icon size="2" as={SearchIcon} />}
                />
                <Input
                  rounded="md"
                  focusBorderColor="green.500"
                  onChange={(e) => {
                    onSearch(e.target.value);
                  }}
                />
              </InputGroup>

              <Flex>
                <Popover>
                  <PopoverTrigger>
                    <Button rightIcon={<MdFilterList />}>Filters</Button>
                  </PopoverTrigger>
                  <Portal>
                    <PopoverContent>
                      <PopoverArrow />
                      <PopoverHeader>Filters</PopoverHeader>
                      <PopoverCloseButton />
                      <PopoverBody>
                        <Flex p="2" flexDirection="column">
                          <Flex>
                            <Flex flexDirection="column">
                              <Text ml="2">Min</Text>
                              <NumberInput
                                size="md"
                                min={1}
                                defaultValue={range.min}
                                onChange={(price: any) => {
                                  onChangeRange({
                                    ...range,
                                    min: Number(price),
                                  });
                                }}
                                mx="2"
                              >
                                <NumberInputField />
                                <NumberInputStepper>
                                  <NumberIncrementStepper />
                                  <NumberDecrementStepper />
                                </NumberInputStepper>
                              </NumberInput>
                            </Flex>
                            <Flex flexDirection="column">
                              <Text ml="2">Max</Text>
                              <NumberInput
                                size="md"
                                min={1}
                                defaultValue={range?.max || null}
                                onChange={(price: any) => {
                                  onChangeRange({
                                    ...range,
                                    max: Number(price),
                                  });
                                }}
                                mx="2"
                              >
                                <NumberInputField />
                                <NumberInputStepper>
                                  <NumberIncrementStepper />
                                  <NumberDecrementStepper />
                                </NumberInputStepper>
                              </NumberInput>
                            </Flex>
                          </Flex>
                        </Flex>
                      </PopoverBody>
                    </PopoverContent>
                  </Portal>
                </Popover>
                <ButtonGroup isAttached mr="2">
                  <ButtonGroup>
                    {refetchLoading ? (
                      <Button mr="2">
                        <Spinner size="sm" />
                      </Button>
                    ) : (
                      <IconButton
                        aria-label="More"
                        bg="gray.200"
                        icon={<FaSync />}
                        isDisabled={loading}
                        onClick={refetchCommodities}
                        ml="2"
                      />
                    )}
                  </ButtonGroup>
                </ButtonGroup>
              </Flex>
            </Flex>
          </SimpleGrid>
        </Box>
      )}

      <Box mt="6">
        {loading ? (
          <Center py="5">
            <Spinner />
          </Center>
        ) : isLargerThan900 ? (
          <Box p="3" height="70vh" overflow="auto-scroll">
            {refetchLoading && (
              <Center py="5">
                <Spinner />
              </Center>
            )}
            <Table variant="simple" size={isLargerThan900 ? "md" : "sm"}>
              <Thead>
                <Tr>
                  <Th>Name</Th>
                  <Th>Price</Th>
                  <Th>Source</Th>
                  <Td></Td>
                </Tr>
              </Thead>
              <Tbody>
                {loading ? (
                  <Center>
                    <Spinner />
                  </Center>
                ) : (
                  commodities.map((commodity: any) => {
                    return (
                      <Tr>
                        <Td>{commodity.name}</Td>
                        <Td>₱ {commodity.price || "N/A"}</Td>
                        <Td>{commodity.source}</Td>
                        <Td></Td>
                      </Tr>
                    );
                  })
                )}
              </Tbody>
            </Table>
          </Box>
        ) : (
          <>
            {refetchLoading && (
              <Center py="5">
                <Spinner />
              </Center>
            )}
            <Table variant="simple" size={isLargerThan900 ? "md" : "sm"}>
              <Thead>
                <Tr>
                  <Th>Name</Th>
                  <Th>Price</Th>
                  <Th>Source</Th>
                  <Td></Td>
                </Tr>
              </Thead>
              <Tbody>
                {loading ? (
                  <Center>
                    <Spinner />
                  </Center>
                ) : (
                  commodities.map((commodity: any) => {
                    return (
                      <Tr>
                        <Td>{commodity.name}</Td>
                        <Td>₱ {commodity.price || "N/A"}</Td>
                        <Td>{commodity.source}</Td>
                        <Td></Td>
                      </Tr>
                    );
                  })
                )}
              </Tbody>
            </Table>
          </>
        )}
      </Box>
    </>
  );
};

export default Clone;
